import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle2, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GIRAFFE_MASCOT = '/images/simby-mascote.png';

// Perguntas de personalização (antes faziam parte do onboarding obrigatório;
// agora ficam opcionais, acessíveis pela aba Plano — briefing 4.1/6).
const QUESTIONS = [
  {
    id: 'contexto_profissional',
    label: 'Contexto profissional',
    question: 'Qual melhor descreve você hoje?',
    options: [
      'Lidero uma equipe',
      'Faço parte de uma equipe',
      'Trabalho de forma autônoma',
      'Estou em transição de carreira',
    ],
  },
  {
    id: 'ambiente_atuacao',
    label: 'Ambiente de atuação',
    question: 'Onde você mais aplica sua comunicação hoje?',
    options: [
      'Empresa',
      'Negócio próprio',
      'Projetos sociais / terceiro setor',
      'Relações pessoais',
    ],
  },
  {
    id: 'principal_desafio',
    label: 'Principal desafio atual',
    question: 'O que mais tem sido desafiador nas suas relações hoje?',
    options: [
      'Dar ou receber feedback',
      'Lidar com conflitos',
      'Ser ouvida(o) / se posicionar',
      'Gerenciar emoções em conversas difíceis',
      'Alinhar expectativas com clareza',
    ],
  },
  {
    id: 'intencao_principal',
    label: 'Intenção principal',
    question: 'O que você mais deseja desenvolver agora?',
    options: [
      'Mais clareza ao me comunicar',
      'Mais segurança emocional nas conversas',
      'Melhorar minhas relações profissionais',
      'Criar ambientes mais seguros (liderança)',
      'Resolver conflitos com mais consciência',
    ],
  },
  {
    id: 'situacao_real',
    label: 'Situação real (opcional)',
    question: 'Se quiser, compartilhe uma situação real que você gostaria de lidar melhor:',
    optional: true,
  },
];

const REQUIRED_IDS = QUESTIONS.filter(q => !q.optional).map(q => q.id);

const PersonalizationSurvey = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const [currentQIdx, setCurrentQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [situacaoReal, setSituacaoReal] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQ = QUESTIONS[currentQIdx];
  const isLastQ = currentQIdx === QUESTIONS.length - 1;
  const canAdvance = currentQ.optional || answers[currentQ.id] !== undefined;
  const allRequiredAnswered = REQUIRED_IDS.every(id => answers[id] !== undefined);
  const answeredCount = REQUIRED_IDS.filter(id => answers[id] !== undefined).length;

  const handleSelect = (option) => setAnswers(prev => ({ ...prev, [currentQ.id]: option }));
  const handleNext = () => { if (currentQIdx < QUESTIONS.length - 1) setCurrentQIdx(i => i + 1); };
  const handlePrev = () => { if (currentQIdx > 0) setCurrentQIdx(i => i - 1); };

  const handleSubmit = async () => {
    if (!allRequiredAnswered || isSubmitting) return;
    setIsSubmitting(true);
    const payload = { ...answers, situacao_real: situacaoReal.trim() || null };
    try {
      const res = await axios.post(`${API}/auth/onboarding`, payload, { withCredentials: true });
      updateUser(res.data);
    } catch (err) {
      console.warn('Personalization submit warning (non-blocking):', err?.message);
    }
    navigate('/pdi');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <img src={GIRAFFE_MASCOT} alt="Simby" className="w-14 h-20 object-contain mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-gray-900">Um pouco sobre você</h2>
          <p className="text-gray-500 mt-1 text-sm">
            Leva menos de 1 minuto. Suas respostas ajudam a personalizar sua jornada.
          </p>

          <div className="mt-5 max-w-sm mx-auto">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{answeredCount} de {REQUIRED_IDS.length} respondidas</span>
              <span>{currentQIdx + 1} de {QUESTIONS.length}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentQIdx / QUESTIONS.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <Card className="p-5">
          <div className="mb-1">
            <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">
              {currentQ.label}
            </span>
          </div>
          <p className="font-semibold text-gray-900 mb-4 leading-snug">{currentQ.question}</p>

          {currentQ.optional ? (
            <textarea
              rows={3}
              placeholder="(opcional) Descreva brevemente..."
              value={situacaoReal}
              onChange={e => setSituacaoReal(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none placeholder-gray-400"
            />
          ) : (
            <div className="space-y-2">
              {currentQ.options.map((option) => {
                const selected = answers[currentQ.id] === option;
                return (
                  <button
                    key={option}
                    onClick={() => handleSelect(option)}
                    className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm transition-all duration-150 flex items-center justify-between ${
                      selected
                        ? 'border-purple-500 bg-purple-50 text-purple-800 font-medium'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <span>{option}</span>
                    {selected && <CheckCircle2 className="w-5 h-5 text-purple-500 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        <div className="mt-4 flex justify-between gap-3">
          <Button variant="outline" onClick={currentQIdx === 0 ? () => navigate('/pdi') : handlePrev} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            {currentQIdx === 0 ? 'Sair' : 'Anterior'}
          </Button>

          {isLastQ ? (
            <Button
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              disabled={!allRequiredAnswered || isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Salvando…' : 'Concluir'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              disabled={!canAdvance}
              onClick={handleNext}
            >
              Próxima
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>

        <p className="text-xs text-gray-400 text-center mt-3">
          Suas respostas são privadas e usadas apenas para personalizar sua jornada.
        </p>
      </div>
    </div>
  );
};

export default PersonalizationSurvey;
